/**
 * Room the floating Your IMAI widget takes from the content under it. The
 * widget publishes these as CSS variables on the root element while it is
 * shown; content that ends under it (the Drive list's last row, the gallery
 * strip's last tile) adds them as scroll padding so it can scroll clear.
 */

/** Height above the viewport bottom the widget covers, or 0px. */
export const FLOATING_ASSISTANT_INSET_VAR = '--floating-assistant-inset';
/** Width from the viewport's right edge the widget covers, or 0px. */
export const FLOATING_ASSISTANT_INSET_RIGHT_VAR =
  '--floating-assistant-inset-right';

/** Space kept between the widget and the content scrolled clear of it. */
const CLEARANCE_PX = 8;

type Rect = { left: number; top: number; width: number; height: number };

export type FloatingAssistantInsets = { bottom: number; right: number };

/**
 * Only a widget resting on the bottom edge covers where content ends; one
 * dragged up the page covers content that scrolls past it anyway. The right
 * inset applies only when the widget sits on the right half (its default
 * corner); on the left it is over the sidebar side, where nothing ends.
 */
export function floatingAssistantInsets(args: {
  panel: Rect;
  viewport: { width: number; height: number };
}): FloatingAssistantInsets {
  const { panel, viewport } = args;
  if (panel.width <= 0 || panel.height <= 0) return { bottom: 0, right: 0 };
  const panelBottom = panel.top + panel.height;
  const gapBelow = viewport.height - panelBottom;
  if (gapBelow > panel.height || panel.top >= viewport.height) {
    return { bottom: 0, right: 0 };
  }
  const bottom = Math.max(
    0,
    Math.ceil(viewport.height - panel.top + CLEARANCE_PX)
  );
  const onRight = panel.left + panel.width / 2 > viewport.width / 2;
  const right = onRight
    ? Math.max(0, Math.ceil(viewport.width - panel.left + CLEARANCE_PX))
    : 0;
  return { bottom, right };
}

/**
 * Root CSS variable the docked artifact panel publishes: how far from the
 * viewport's right edge the chat column ends. The idle widget's boundary
 * stops there, so it docks inside the chat column and never over the panel.
 */
export const ARTIFACT_PANEL_ASSISTANT_RIGHT_VAR =
  '--artifact-panel-assistant-right';
/**
 * RTL counterpart: in Arabic the sidebar stays on the left and the panel
 * docks beside it, so the chat column (and the widget's boundary) starts at
 * the panel's right edge (#7451).
 */
export const ARTIFACT_PANEL_ASSISTANT_LEFT_VAR =
  '--artifact-panel-assistant-left';
/** Root attribute set while the panel leaves no chat column for the widget. */
export const ARTIFACT_PANEL_HIDES_ASSISTANT_ATTR =
  'data-artifact-panel-hides-assistant';

/** Narrowest chat column the idle widget (w-36 plus margins) still fits in. */
export const FLOATING_ASSISTANT_MIN_CHAT_WIDTH_PX = 176;

export type FloatingAssistantPanelPlacement =
  | { mode: 'viewport' }
  /** Chat column bounds: `right` from the viewport's right edge, `left` from its left edge when the panel sits on the left (RTL). */
  | { mode: 'chat'; right: number; left?: number }
  | { mode: 'hidden' };

/**
 * Where the idle Your IMAI widget may sit while an artifact panel is open.
 * No panel (or one not docked on the right edge): the whole viewport, as
 * before. A docked panel: the chat column to its left, ending where the
 * panel starts. A full-screen panel, or one that leaves the chat column too
 * narrow for the widget: hidden, because any place would cover the panel.
 */
export function floatingAssistantPanelPlacement(args: {
  viewportWidth: number;
  /** The panel's rect, or null when no panel is open. */
  panel: { left: number; width: number } | null;
  fullscreen: boolean;
  /** Left edge of the content area (after the sidebar). */
  contentLeft?: number;
  /** Document direction: in RTL the panel docks on the left of the chat. */
  rtl?: boolean;
}): FloatingAssistantPanelPlacement {
  const { viewportWidth, panel, fullscreen } = args;
  if (!panel || panel.width <= 0) return { mode: 'viewport' };
  if (fullscreen) return { mode: 'hidden' };
  const panelRight = panel.left + panel.width;
  // A panel docked against the right edge splits the screen (LTR). In RTL
  // it docks on the left and the chat column runs from its right edge to
  // the viewport's.
  if (Math.abs(viewportWidth - panelRight) > 2) {
    if (!args.rtl) return { mode: 'viewport' };
    if (viewportWidth - panelRight < FLOATING_ASSISTANT_MIN_CHAT_WIDTH_PX) {
      return { mode: 'hidden' };
    }
    return { mode: 'chat', right: 0, left: Math.floor(panelRight) };
  }
  const right = Math.max(0, Math.ceil(viewportWidth - panel.left));
  const chatWidth = panel.left - Math.max(0, args.contentLeft ?? 0);
  if (chatWidth < FLOATING_ASSISTANT_MIN_CHAT_WIDTH_PX) {
    return { mode: 'hidden' };
  }
  return { mode: 'chat', right };
}

/** True while a docked artifact panel confines the widget to the chat column. */
export function isDockedBesideArtifactPanel(): boolean {
  if (typeof document === 'undefined') return false;
  const style = document.documentElement.style;
  return (
    style.getPropertyValue(ARTIFACT_PANEL_ASSISTANT_RIGHT_VAR) !== '' ||
    style.getPropertyValue(ARTIFACT_PANEL_ASSISTANT_LEFT_VAR) !== ''
  );
}
